'use client';

import { useState } from 'react';

type Props = {
  id: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
};

export default function PasswordInput({ id, value, onChange, autoComplete, required, minLength }: Props) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="auth-password-wrap">
      <input
        id={id}
        className="auth-input auth-password-input"
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className={`auth-pw-btn${visible ? ' auth-pw-btn--on' : ''}`}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        tabIndex={-1}
      >
        <span className="auth-pw-ripple" />

        {/* Eye — visible when hidden */}
        <span className="auth-pw-icon auth-pw-icon--eye">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"
              stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="2.5" fill="currentColor" />
            <circle cx="12" cy="12" r="1" fill="white" opacity="0.6" />
          </svg>
        </span>

        {/* Eye-off — visible when shown */}
        <span className="auth-pw-icon auth-pw-icon--eyeoff">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            <path d="M10.58 10.58A3 3 0 0 0 14.83 14.83"
              stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            <path d="M9.36 5.37A10.92 10.92 0 0 1 12 5c7 0 11 7 11 7a20.6 20.6 0 0 1-3.24 4.27"
              stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6.61 6.61A20.15 20.15 0 0 0 1 12s4 7 11 7a10.87 10.87 0 0 0 5.39-1.36"
              stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      <style>{`
        .auth-password-wrap {
          position: relative;
        }
        .auth-password-input {
          padding-right: 2.75rem;
        }

        /* ── Button shell ── */
        .auth-pw-btn {
          position: absolute;
          top: 50%;
          right: 0.55rem;
          transform: translateY(-50%);
          width: 2rem;
          height: 2rem;
          background: transparent;
          border: none;
          padding: 0;
          cursor: pointer;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          overflow: hidden;
          transition: color 0.25s ease, background 0.25s ease, transform 0.18s ease;
        }
        .auth-pw-btn:hover {
          color: var(--color-primary);
          background: rgba(27, 63, 35, 0.08);
          transform: translateY(-50%) scale(1.1);
        }
        .auth-pw-btn--on {
          color: var(--color-primary);
        }
        .auth-pw-btn:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
        .auth-pw-btn:active {
          transform: translateY(-50%) scale(0.92) !important;
          transition: transform 0.08s ease;
        }

        /* ── Ripple ── */
        .auth-pw-ripple {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(27,63,35,0.18) 0%, transparent 70%);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }
        .auth-pw-btn:active .auth-pw-ripple {
          opacity: 1;
          transition: opacity 0s;
        }

        /* ── Icon layers ── */
        .auth-pw-icon {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          transition:
            opacity 0.22s ease,
            transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Eye: shown by default, hides when --on */
        .auth-pw-icon--eye {
          opacity: 1;
          transform: scale(1) rotate(0deg);
        }
        .auth-pw-btn--on .auth-pw-icon--eye {
          opacity: 0;
          transform: scale(0.4) rotate(-30deg);
        }

        /* Eye-off: hidden by default, shows when --on */
        .auth-pw-icon--eyeoff {
          opacity: 0;
          transform: scale(0.4) rotate(30deg);
        }
        .auth-pw-btn--on .auth-pw-icon--eyeoff {
          opacity: 1;
          transform: scale(1) rotate(0deg);
        }
      `}</style>
    </div>
  );
}
